import type {
  ConformanceRunOutcome,
  ConformanceSkipReason,
} from "../conformance-outcome.js";
import type { MCPServerConfig } from "../mcp-client-manager/index.js";
import type { TasksWire } from "../mcp-client-manager/tasks-dispatch.js";

export const MCP_TASKS_CHECK_CATEGORIES = [
  "dispatch",
  "creation",
  "lifecycle",
] as const;

export type MCPTasksCheckCategory = (typeof MCP_TASKS_CHECK_CATEGORIES)[number];

export const MCP_TASKS_CHECK_IDS = [
  "tasks-wire-resolvable",
  "tasks-declaration-hygiene",
  "tasks-result-type-discipline",
  "tasks-undeclared-creation-refused",
  "tasks-undeclared-capability-rejected",
  "tasks-ttl-shape",
  "tasks-inline-result",
  "tasks-mcp-name-routing",
] as const;

export type MCPTasksCheckId = (typeof MCP_TASKS_CHECK_IDS)[number];

export type MCPTasksCheckStatus = "passed" | "failed" | "skipped";

/**
 * Why a selected check produced no verdict. Every `skipped` check carries one,
 * and the two are NOT interchangeable:
 *
 *   - `"not-applicable"` — the check cannot apply to THIS server, so there is
 *     nothing to establish: an extension-only check on a legacy connection, the
 *     `Mcp-Name` check on stdio, any task check on a connection with no tasks
 *     wire. A run may still pass with these.
 *   - `"could-not-run"` — the check DOES apply and was selected, but the run
 *     could not exercise it: no probe tool resolved, the probe tool is not
 *     listed, no task was created, the task never became readable. The run is
 *     then `incomplete`, never `passed` — "did not run" must never be read as
 *     "conformed".
 */
/** @see {@link ConformanceSkipReason} — the vocabulary is shared by every suite. */
export type MCPTasksSkipReason = ConformanceSkipReason;

/**
 * A run's verdict.
 *
 *   - `"passed"` — every selected, applicable check ran and passed.
 *   - `"failed"` — at least one check produced a violation.
 *   - `"incomplete"` — nothing failed, but at least one selected check could
 *     not be run, so the run does not establish conformance.
 */
/** @see {@link ConformanceRunOutcome} — the vocabulary is shared by every suite. */
export type MCPTasksRunOutcome = ConformanceRunOutcome;

export interface MCPTasksCheckResult {
  id: MCPTasksCheckId;
  category: MCPTasksCheckCategory;
  title: string;
  description: string;
  status: MCPTasksCheckStatus;
  /** Always set when `status` is `"skipped"`. */
  skipReason?: MCPTasksSkipReason;
  durationMs: number;
  error?: {
    message: string;
    details?: unknown;
  };
  details?: Record<string, unknown>;
  warnings?: string[];
}

export type MCPTasksConformanceConfig = MCPServerConfig & {
  checkIds?: MCPTasksCheckId[];
  /**
   * Tool used to provoke a task. Without it the runner picks the first tool
   * whose `execution.taskSupport` is `required`, then the first with
   * `optional` — 2025-11-25 metadata that the 2026-07-28 `ToolSchema` strips,
   * so auto-selection cannot work on the extension wire. When no probe tool
   * resolves, every task-dependent check reports `could-not-run` and the run
   * is `incomplete`; it never passes on skips.
   */
  toolName?: string;
  toolArguments?: Record<string, unknown>;
  /** Upper bound on polling a created task to a terminal status. */
  pollTimeoutMs?: number;
};

export interface NormalizedMCPTasksConformanceConfig {
  serverConfig: MCPServerConfig;
  target: string;
  timeout: number;
  pollTimeoutMs: number;
  checkIds?: MCPTasksCheckId[];
  toolName?: string;
  toolArguments?: Record<string, unknown>;
}

export interface MCPTasksConformanceResult {
  /**
   * True ONLY when `outcome` is `"passed"`: every selected check either ran and
   * passed or was inapplicable to this server. A check that could not run keeps
   * this false, so a skip can never add up to a green run.
   */
  passed: boolean;
  outcome: MCPTasksRunOutcome;
  /**
   * Present when `outcome` is `"incomplete"`: which checks did not run and what
   * the caller has to change to make them run.
   */
  incompleteReason?: string;
  target: string;
  checks: MCPTasksCheckResult[];
  summary: string;
  durationMs: number;
  categorySummary: Record<
    MCPTasksCheckCategory,
    {
      total: number;
      passed: number;
      failed: number;
      /** Every skip, of either reason. */
      skipped: number;
      /** The subset of `skipped` that is `"could-not-run"`. */
      couldNotRun: number;
    }
  >;
  discovery: {
    protocolVersion?: string;
    wire: TasksWire;
    toolCount: number;
    taskCapableToolCount: number;
    probedTool?: string;
    createdTaskId?: string;
  };
}
